import React, { forwardRef, useCallback, useRef, useState, useEffect } from 'react';
import './BackButton.css';

export type BackButtonVariant = 'arrow' | 'close' | 'menu';
export type BackButtonSize = 'small' | 'medium' | 'large';
export type BackButtonAction = 'navigate' | 'close' | 'dismiss' | 'custom';

export interface BackButtonProps {
  /** Вариант кнопки: arrow (стрелка назад), close (крестик), menu (три точки) */
  variant?: BackButtonVariant;
  /** Размер кнопки */
  size?: BackButtonSize;
  /** Обработчик клика */
  onClick: () => void;
  /** Тип действия для семантики и дополнительной логики */
  action?: BackButtonAction;
  /** Текст кнопки (опционально, если нужна кнопка с текстом) */
  label?: string;
  /** Заголовок для доступности */
  title?: string;
  /** Дополнительный CSS класс */
  className?: string;
  /** Отключена ли кнопка */
  disabled?: boolean;
  /** Прозрачный фон (для overlay на изображениях) */
  transparent?: boolean;
  /** Показывать ripple эффект при клике */
  ripple?: boolean;
  /** Задержка перед выполнением onClick (мс) для плавной анимации */
  animationDelay?: number;
  /** Предотвращать всплытие события */
  stopPropagation?: boolean;
  /** ID для тестирования */
  testId?: string;
  /** Пользовательская иконка (RemixIcon класс) */
  customIcon?: string;
  /** Callback при наведении */
  onHover?: (isHovered: boolean) => void;
  /** Показывать тултип */
  showTooltip?: boolean;
  /** Inline стили */
  style?: React.CSSProperties;
}

/**
 * Профессиональная универсальная кнопка "назад" / "закрыть" / "меню"
 * 
 * Особенности:
 * - Поддержка различных вариантов и размеров
 * - Ripple эффект для лучшего UX
 * - Полная поддержка accessibility (WCAG 2.1)
 * - Оптимизированная производительность с мемоизацией
 * - Поддержка различных действий и контекстов
 * - Гибкая система стилизации
 * 
 * @example
 * // Базовое использование
 * <BackButton onClick={handleBack} />
 * 
 * @example
 * // Кнопка закрытия с ripple эффектом
 * <BackButton variant="close" onClick={handleClose} ripple />
 * 
 * @example
 * // Кнопка меню с кастомной иконкой
 * <BackButton variant="menu" customIcon="ri-settings-line" onClick={handleMenu} />
 */
const BackButton = forwardRef<HTMLButtonElement, BackButtonProps>(({
  variant = 'arrow',
  size = 'medium',
  onClick,
  action = 'custom',
  label,
  title,
  className = '',
  disabled = false,
  transparent = false,
  ripple = false,
  animationDelay = 0,
  stopPropagation = true,
  testId,
  customIcon,
  onHover,
  showTooltip = true,
  style
}, ref) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isPressed, setIsPressed] = useState(false);
  const [rippleEffect, setRippleEffect] = useState<{ x: number; y: number; show: boolean } | null>(null);

  // Объединяем ref из props и внутренний ref
  useEffect(() => {
    if (typeof ref === 'function') {
      ref(buttonRef.current);
    } else if (ref) {
      ref.current = buttonRef.current;
    }
  }, [ref]);

  // Получить иконку
  const getIcon = useCallback(() => {
    if (customIcon) return customIcon;
    
    switch (variant) {
      case 'close':
        return 'ri-close-line';
      case 'menu':
        return 'ri-more-2-line';
      case 'arrow':
      default:
        return 'ri-arrow-left-s-line';
    }
  }, [variant, customIcon]);

  // Получить заголовок
  const getTitle = useCallback(() => {
    if (title) return title;
    
    switch (action) {
      case 'navigate':
        return 'Вернуться назад';
      case 'close':
        return 'Закрыть';
      case 'dismiss':
        return 'Отменить';
      default:
        switch (variant) {
          case 'close':
            return 'Закрыть';
          case 'menu':
            return 'Открыть меню';
          default:
            return 'Назад';
        }
    }
  }, [title, action, variant]);

  // Обработчик ripple эффекта
  const handleRipple = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (!ripple || disabled) return;
    
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setRippleEffect({ x, y, show: true });
    
    setTimeout(() => {
      setRippleEffect(null);
    }, 600);
  }, [ripple, disabled]);

  // Обработчик клика
  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    
    if (disabled) return;
    
    setIsPressed(true);
    handleRipple(e);
    
    // Выполняем onClick с задержкой для плавной анимации
    if (animationDelay > 0) {
      setTimeout(() => {
        onClick();
        setIsPressed(false);
      }, animationDelay);
    } else {
      onClick();
      setTimeout(() => setIsPressed(false), 150);
    }
  }, [onClick, disabled, animationDelay, stopPropagation, handleRipple]);

  // Обработчик наведения
  const handleMouseEnter = useCallback(() => {
    if (onHover && !disabled) {
      onHover(true);
    }
  }, [onHover, disabled]);

  const handleMouseLeave = useCallback(() => {
    if (onHover && !disabled) {
      onHover(false);
    }
  }, [onHover, disabled]);

  // Обработка клавиатуры для accessibility
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    
    // Enter или пробел
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsPressed(true);
    }
  }, [disabled]);

  const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
      setIsPressed(false);
    }
  }, [onClick, disabled]);

  // Собираем классы
  const classNames = [
    'back-button',
    `back-button--${variant}`,
    `back-button--${size}`,
    `back-button--${action}`,
    transparent && 'back-button--transparent',
    label && 'back-button--with-label',
    isPressed && 'back-button--pressed',
    ripple && 'back-button--ripple',
    disabled && 'back-button--disabled',
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      ref={buttonRef}
      className={classNames}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      title={showTooltip ? getTitle() : undefined}
      aria-label={getTitle()}
      aria-pressed={isPressed}
      aria-disabled={disabled}
      disabled={disabled}
      type="button"
      data-testid={testId}
      data-action={action}
      data-variant={variant}
      style={style}
    >
      <i className={getIcon()} aria-hidden="true"></i>
      {label && <span className="back-button__label">{label}</span>}
      
      {/* Ripple эффект */}
      {rippleEffect && rippleEffect.show && (
        <span 
          className="back-button__ripple"
          style={{
            left: rippleEffect.x,
            top: rippleEffect.y
          }}
        />
      )}
    </button>
  );
});

BackButton.displayName = 'BackButton';

export default BackButton;
